/**
 * @file FastItemPickerModal.tsx
 * @description Rapid order entry modal with one-tap category tabs and modifiers.
 */

import React, { useState } from 'react';
import { X, Plus, Check } from 'lucide-react';
import type { MenuItem, MenuItemModifier } from '../../../types/waiter.types';
import type { MenuCategory } from '../services/menuCatalog.service';

interface FastItemPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: MenuCategory[];
  menuItems: MenuItem[];
  currency?: string;
  onAddItem: (
    item: MenuItem,
    cookingStyle?: string | null,
    modifiers?: MenuItemModifier[],
    notes?: string | null,
    course?: 'starter' | 'main' | 'dessert' | 'drinks'
  ) => void;
}

export const FastItemPickerModal: React.FC<FastItemPickerModalProps> = ({
  isOpen,
  onClose,
  categories,
  menuItems,
  currency = 'AED',
  onAddItem,
}) => {
  const [activeCat, setActiveCat] = useState<string>('all');
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [cookingStyle, setCookingStyle] = useState<string | null>(null);
  const [selectedModifiers, setSelectedModifiers] = useState<MenuItemModifier[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [course, setCourse] = useState<'starter' | 'main' | 'dessert' | 'drinks'>('main');

  if (!isOpen) return null;

  const filteredItems = activeCat === 'all'
    ? menuItems
    : menuItems.filter(i => i.category_id === activeCat);

  const handleSelectItem = (item: MenuItem) => {
    const hasModifierGroups = item.modifier_groups && item.modifier_groups.length > 0;
    const hasCookingStyles = item.cooking_styles && item.cooking_styles.length > 0;
    const hasExtras = item.extras && item.extras.length > 0;

    // Fast-path: If item has no custom options, add immediately to tray
    if (!hasModifierGroups && !hasCookingStyles && !hasExtras) {
      onAddItem(item, null, [], null, 'main');
      return;
    }

    setSelectedItem(item);
    setCookingStyle(item.cooking_styles && item.cooking_styles.length > 0 ? item.cooking_styles[0] : null);

    // Pre-populate any options configured with is_default: true
    const defaultModifiers: MenuItemModifier[] = [];
    if (hasModifierGroups) {
      item.modifier_groups?.forEach(group => {
        group.options.forEach(opt => {
          if (opt.is_default) {
            defaultModifiers.push({ ...opt, quantity: 1 });
          }
        });
      });
    }
    setSelectedModifiers(defaultModifiers);
    setNotes('');
  };

  const handleConfirmCustomizedItem = () => {
    if (!selectedItem) return;
    onAddItem(selectedItem, cookingStyle, selectedModifiers, notes || null, course);
    setSelectedItem(null);
  };

  const handleToggleOption = (group: any, option: MenuItemModifier) => {
    const isSingle = (group.min_selection === 1 && group.max_selection === 1) || group.max_selection === 1;

    setSelectedModifiers(prev => {
      const exists = prev.find(m => m.id === option.id);

      if (isSingle) {
        // Replace previous option in this group
        const withoutGroup = prev.filter(m => m.group_id !== group.id);
        return exists ? withoutGroup : [...withoutGroup, { ...option, quantity: 1 }];
      }

      if (exists) {
        return prev.filter(m => m.id !== option.id);
      } else {
        return [...prev, { ...option, quantity: 1 }];
      }
    });
  };

  const handleAdjustQuantity = (option: MenuItemModifier, delta: number) => {
    setSelectedModifiers(prev => {
      const existing = prev.find(m => m.id === option.id);
      if (!existing) {
        if (delta > 0) return [...prev, { ...option, quantity: 1 }];
        return prev;
      }

      const nextQty = (existing.quantity || 1) + delta;
      if (nextQty <= 0) {
        return prev.filter(m => m.id !== option.id);
      }
      const maxAllowed = option.max_quantity || 10;
      const clampedQty = Math.min(maxAllowed, nextQty);

      return prev.map(m => m.id === option.id ? { ...m, quantity: clampedQty } : m);
    });
  };

  // Computes the total unit price with modifiers and quantities in real time
  const currentModifiersPrice = selectedModifiers.reduce(
    (sum, m) => sum + (Number(m.total_price) || (Number(m.price) || 0) * (m.quantity || 1)),
    0
  );
  const totalUnitPrice = (selectedItem?.price || 0) + currentModifiersPrice;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-2xl bg-zinc-950 border border-zinc-800 rounded-t-3xl sm:rounded-3xl h-[92dvh] sm:h-[85vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Mobile slide-up drag handle */}
        <div className="sm:hidden w-full flex justify-center pt-2.5 pb-1 bg-zinc-900/60 shrink-0">
          <div className="w-10 h-1 rounded-full bg-zinc-700" />
        </div>

        {/* Modal Header */}
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between bg-zinc-900/40 shrink-0">
          <h2 className="text-base font-black text-white uppercase tracking-tight truncate pr-2">
            {selectedItem ? `Customize: ${selectedItem.name}` : 'Select Menu Item'}
          </h2>
          <button
            type="button"
            onClick={() => {
              if (selectedItem) setSelectedItem(null);
              else onClose();
            }}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {selectedItem ? (
          /* Item Customization Mode (Cooking style, Modifier groups, Notes) */
          <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            {/* Dynamic Cooking Style Options */}
            {selectedItem.cooking_styles && selectedItem.cooking_styles.length > 0 && (
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                  Cooking Preference
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {selectedItem.cooking_styles.map(cs => (
                    <button
                      key={cs}
                      type="button"
                      onClick={() => setCookingStyle(cs)}
                      className={`p-3 min-h-[42px] rounded-xl text-xs font-bold text-center border transition-all ${
                        cookingStyle === cs
                          ? 'bg-amber-500 text-black border-amber-500 font-black'
                          : 'bg-zinc-900 text-zinc-300 border-zinc-800 hover:border-zinc-700'
                      }`}
                    >
                      {cs}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Structured Modifier Groups */}
            {selectedItem.modifier_groups && selectedItem.modifier_groups.length > 0 ? (
              selectedItem.modifier_groups.map(group => {
                const isRequired = group.min_selection > 0;
                const isSingle = (group.min_selection === 1 && group.max_selection === 1) || group.max_selection === 1;

                return (
                  <div key={group.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold uppercase text-zinc-300 tracking-wider">
                        {group.name}
                      </label>
                      <div className="flex items-center gap-1.5">
                        {isRequired ? (
                          <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20 uppercase">
                            Required (Min {group.min_selection})
                          </span>
                        ) : (
                          <span className="text-[10px] font-medium text-zinc-500 uppercase">Optional</span>
                        )}
                        {group.free_selections_count > 0 && (
                          <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20">
                            {group.free_selections_count} Free
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      {group.options.map(opt => {
                        const selectedMatch = selectedModifiers.find(m => m.id === opt.id);
                        const isChecked = Boolean(selectedMatch);
                        const currentQty = selectedMatch?.quantity || 0;
                        const isRemoval = opt.is_removal || group.group_type === 'REMOVAL';

                        return (
                          <div
                            key={opt.id}
                            className={`w-full p-3 rounded-xl border flex items-center justify-between transition-all ${
                              isChecked
                                ? isRemoval
                                  ? 'bg-rose-950/20 border-rose-800/60 text-rose-300'
                                  : 'bg-amber-500/10 border-amber-500/50 text-amber-300'
                                : 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700'
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => handleToggleOption(group, opt)}
                              className="flex items-center gap-2.5 flex-1 text-left min-w-0"
                            >
                              <span className={`w-4 h-4 rounded ${isSingle ? 'rounded-full' : 'rounded-md'} flex items-center justify-center border shrink-0 ${
                                isChecked
                                  ? isRemoval
                                    ? 'bg-rose-600 border-rose-600 text-white'
                                    : 'bg-amber-500 border-amber-500 text-black'
                                  : 'border-zinc-700'
                              }`}>
                                {isChecked && <Check className="w-3 h-3 stroke-[3]" />}
                              </span>
                              <div className="truncate">
                                <span className={`text-xs font-semibold ${isRemoval && isChecked ? 'line-through text-rose-300' : ''}`}>
                                  {opt.name}
                                </span>
                              </div>
                            </button>

                            <div className="flex items-center gap-2 shrink-0">
                              {Number(opt.price) > 0 ? (
                                <span className="text-xs font-mono font-bold text-zinc-400">
                                  +{opt.price} {currency}
                                </span>
                              ) : (
                                <span className="text-[10px] font-mono text-zinc-500 uppercase">Free</span>
                              )}

                              {/* Stepper controls if group allows option quantities */}
                              {group.allow_option_quantities && (
                                <div className="flex items-center gap-1.5 ml-2 bg-zinc-800 px-1 py-0.5 rounded-lg border border-zinc-700">
                                  <button
                                    type="button"
                                    onClick={() => handleAdjustQuantity(opt, -1)}
                                    className="w-6 h-6 flex items-center justify-center rounded text-zinc-400 hover:text-white hover:bg-zinc-700 font-bold"
                                  >
                                    -
                                  </button>
                                  <span className="w-5 text-center font-mono text-xs font-bold text-zinc-100">
                                    {currentQty}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleAdjustQuantity(opt, 1)}
                                    className="w-6 h-6 flex items-center justify-center rounded text-zinc-400 hover:text-white hover:bg-zinc-700 font-bold"
                                  >
                                    +
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            ) : selectedItem.extras && selectedItem.extras.length > 0 ? (
              /* Fallback for items with flat extras */
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                  Add Extras
                </label>
                <div className="space-y-2">
                  {selectedItem.extras.map(ex => {
                    const isChecked = selectedModifiers.some(m => m.id === ex.id);
                    return (
                      <button
                        key={ex.id}
                        type="button"
                        onClick={() => handleToggleOption({ id: 'extras', min_selection: 0, max_selection: null }, ex)}
                        className={`w-full p-3.5 min-h-[44px] rounded-xl text-xs font-bold flex items-center justify-between border transition-all ${
                          isChecked
                            ? 'bg-amber-500/10 text-amber-300 border-amber-500/50'
                            : 'bg-zinc-900 text-zinc-300 border-zinc-800'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className={`w-4 h-4 rounded flex items-center justify-center border ${
                            isChecked ? 'bg-amber-500 border-amber-500 text-black' : 'border-zinc-700'
                          }`}>
                            {isChecked && <Check className="w-3 h-3 stroke-[3]" />}
                          </span>
                          <span>{ex.name}</span>
                        </span>
                        <span className="font-mono text-zinc-400">+{ex.price} {currency}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {/* Course Selector */}
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                Course Timing
              </label>
              <div className="grid grid-cols-4 gap-2">
                {(['starter', 'main', 'dessert', 'drinks'] as const).map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCourse(c)}
                    className={`py-2.5 px-1 min-h-[42px] rounded-xl text-[11px] font-bold uppercase text-center border transition-all ${
                      course === c
                        ? 'bg-zinc-100 text-black border-white font-black'
                        : 'bg-zinc-900 text-zinc-400 border-zinc-800'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Preparation Notes */}
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                Special Instructions
              </label>
              <input
                type="text"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="e.g. No onions, dressing on side..."
                className="w-full px-4 py-3 min-h-[44px] rounded-xl bg-zinc-900 border border-zinc-800 text-base sm:text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-amber-500"
              />
            </div>

            <button
              type="button"
              onClick={handleConfirmCustomizedItem}
              className="w-full py-4 min-h-[48px] rounded-2xl bg-amber-500 hover:bg-amber-400 active:scale-[0.98] text-black font-black text-sm tracking-wider uppercase shadow-lg shadow-amber-500/20 transition-all mt-4 cursor-pointer flex items-center justify-center gap-2"
            >
              <span>Add to Order Tray</span>
              <span className="font-mono">({totalUnitPrice.toFixed(2)} {currency})</span>
            </button>
          </div>
        ) : (
          /* Catalog Browsing Mode */
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Category Tabs */}
            <div className="flex items-center gap-1.5 p-3 overflow-x-auto border-b border-zinc-800 shrink-0 scrollbar-none">
              <button
                type="button"
                onClick={() => setActiveCat('all')}
                className={`px-3.5 py-2 min-h-[40px] rounded-xl text-xs font-bold whitespace-nowrap transition-colors ${
                  activeCat === 'all'
                    ? 'bg-amber-500 text-black font-black'
                    : 'bg-zinc-900 text-zinc-400 border border-zinc-800'
                }`}
              >
                All Items
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveCat(cat.id)}
                  className={`px-3.5 py-2 min-h-[40px] rounded-xl text-xs font-bold whitespace-nowrap transition-colors ${
                    activeCat === cat.id
                      ? 'bg-amber-500 text-black font-black'
                      : 'bg-zinc-900 text-zinc-400 border border-zinc-800'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Item List */}
            <div className="flex-1 overflow-y-auto p-3 grid grid-cols-1 sm:grid-cols-2 gap-2 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              {filteredItems.map(item => {
                const isAutoStock = Boolean(item.availability_settings?.auto_stock_enabled);
                const remainingStock = isAutoStock ? (item.availability_settings?.remaining_stock ?? 0) : null;
                const isOut = isAutoStock && (remainingStock ?? 0) <= 0;
                const isLow = isAutoStock && (remainingStock ?? 0) <= (item.availability_settings?.low_stock_threshold ?? 5) && !isOut;

                return (
                  <button
                    key={item.id}
                    type="button"
                    disabled={isOut}
                    onClick={() => handleSelectItem(item)}
                    className={`p-3.5 min-h-[64px] rounded-2xl bg-zinc-900/80 border flex items-center justify-between text-left transition-all ${
                      isOut
                        ? 'opacity-40 border-zinc-800 cursor-not-allowed'
                        : 'border-zinc-800/80 hover:border-amber-500/50 active:scale-[0.98] cursor-pointer'
                    }`}
                  >
                    <div className="min-w-0 pr-2">
                      <p className="font-bold text-sm text-zinc-100 truncate">{item.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs font-mono font-black text-amber-400">
                          {item.price.toFixed(2)} {currency}
                        </span>
                        {isAutoStock && (
                          <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border uppercase tracking-wider ${
                            isOut
                              ? 'bg-rose-500/20 text-rose-300 border-rose-500/40'
                              : isLow
                              ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                              : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                          }`}>
                            {isOut ? 'Sold Out' : `${remainingStock} left`}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="w-9 h-9 rounded-xl bg-zinc-800 text-zinc-300 flex items-center justify-center shrink-0 border border-zinc-700">
                      <Plus className="w-4 h-4" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

